/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import useIsMobile from 'hooks/useIsMobile';
import { SearchContext } from 'components/views/search/context/SearchContext';
import { SET_AUTOCOMPLETE_PANEL_ACTIVE } from 'components/views/search/reducer/index';
import { KEY_VALUES } from 'helpers/constants';

import Grid from 'components/designSystem/Grid/Grid.tsx';
import MenuPrimary from 'components/designSystem/MenuPrimary/MenuPrimary';
import SearchPrimary from 'components/designSystem/SearchPrimary/SearchPrimary';

import PictoPizza40 from 'components/shared/SVG/PictoPizza40';
import PictoAmerican40 from 'components/shared/SVG/PictoAmerican40';
import PictoMexican40 from 'components/shared/SVG/PictoMexican40';
import PictoItalian40 from 'components/shared/SVG/PictoItalian40';
import PictoSandwiches40 from 'components/shared/SVG/PictoSandwiches40';
import PictoCafe40 from 'components/shared/SVG/PictoCafe40';
import PictoBurgers40 from 'components/shared/SVG/PictoBurgers40';
import PictoThai40 from 'components/shared/SVG/PictoThai40';
import PictoDeli40 from 'components/shared/SVG/PictoDeli40';
import PictoBreakfast40 from 'components/shared/SVG/PictoBreakfast40';
import PictoBarbecue40 from 'components/shared/SVG/PictoBarbecue40';
import PictoAsianFusion40 from 'components/shared/SVG/PictoAsianFusion40';
import PictoJapanese40 from 'components/shared/SVG/PictoJapanese40';
import PictoSushi40 from 'components/shared/SVG/PictoSushi40';
import PictoMediterranean40 from 'components/shared/SVG/PictoMediterranean40';
import PictoIndian40 from 'components/shared/SVG/PictoIndian40';
import { PictoChinese40 } from 'components/shared/SVG/PictoChinese';
import PictoSeafood40 from 'components/shared/SVG/PictoSeafood40';
import PictoVegetarian40 from 'components/shared/SVG/PictoVegetarian40';
import CloseButton24 from 'components/shared/SVG/CloseButton24';
import AutocompleteResults from '../AutocompleteResults/AutocompleteResults';

import styles from './SearchOverlay.module.scss';

export default function SearchOverlay({
  onAutocompleteChange,
  onAutocompleteSearch,
  onSubmit,
  onTileClick,
  showCuisines,
}) {
  const {
    state: { searchFiltersFromURL, geocodedSearchedAddress },
    dispatchToReducer,
  } = useContext(SearchContext);
  const [searchValue, setSearchValue] = useState(searchFiltersFromURL.query);
  const inputRef = useRef();
  const isMobile = useIsMobile();
  const searchSize = isMobile ? 'med' : 'large';
  const categories = [
    { pictogram: PictoPizza40, title: 'Pizza' },
    { pictogram: PictoAmerican40, title: 'American' },
    { pictogram: PictoMexican40, title: 'Mexican' },
    { pictogram: PictoItalian40, title: 'Italian' },
    { pictogram: PictoSandwiches40, title: 'Sandwiches' },
    { pictogram: PictoCafe40, title: 'Cafe' },
    { pictogram: PictoBurgers40, title: 'Burgers' },
    { pictogram: PictoThai40, title: 'Thai' },
    { pictogram: PictoDeli40, title: 'Deli' },
    { pictogram: PictoBreakfast40, title: 'Breakfast & Brunch' },
    { pictogram: PictoBarbecue40, title: 'Barbecue' },
    { pictogram: PictoAsianFusion40, title: 'Asian Fusion' },
    { pictogram: PictoJapanese40, title: 'Japanese' },
    { pictogram: PictoSushi40, title: 'Sushi' },
    { pictogram: PictoMediterranean40, title: 'Mediterranean' },
    { pictogram: PictoIndian40, title: 'Indian' },
    { pictogram: PictoChinese40, title: 'Chinese' },
    { pictogram: PictoSeafood40, title: 'Seafood' },
    { pictogram: PictoVegetarian40, title: 'Vegetarian' },
  ];

  useEffect(() => {
    // One of the features of the search bar is to highlight the input text
    // when the overlay mounts. We must use setSelectionRange since that works
    // on both desktop and mobile devices.
    if (isMobile) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, 999);
    }
  }, []);

  const debounceSearch = useCallback(
    debounce((e) => onAutocompleteChange(e), 500),
    [geocodedSearchedAddress]
  );

  function handleDebounce(e) {
    e.persist();
    setSearchValue(e.target.value);
    debounceSearch(e);
  }

  function handleEnterEscKey(e) {
    if (e.key === KEY_VALUES.enter || e.key === KEY_VALUES.escape)
      dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: false });
  }

  return (
    <div className={styles.wrapper} onKeyDown={handleEnterEscKey} tabIndex="-1" role="none">
      {isMobile && (
        <>
          <div className={styles.searchHeader}>
            Search
            <button
              className={styles.closeButton}
              onClick={() =>
                dispatchToReducer({ type: SET_AUTOCOMPLETE_PANEL_ACTIVE, payload: false })
              }
              type="button"
            >
              <CloseButton24 />
            </button>
          </div>
          <Grid container>
            <Grid sm={4} md={12} lg={12}>
              <div className={styles.searchWrapper}>
                <form
                  action=""
                  autoComplete="false"
                  spellCheck="false"
                  onSubmit={onSubmit}
                  className={styles.searchForm}
                >
                  <SearchPrimary
                    inputRef={inputRef}
                    onChange={handleDebounce}
                    value={searchValue}
                    placeholder="Search for cuisine or restaurant"
                    size={searchSize}
                  />
                </form>
              </div>
            </Grid>
          </Grid>
        </>
      )}

      <div className={styles.overflowWrapper}>
        {showCuisines ? (
          <Grid container className={styles.categoryGrid}>
            {categories.map(({ title, pictogram }) => (
              <Grid key={title} sm={4} md={6} lg={4} className={styles.category}>
                <MenuPrimary
                  handleClick={() => onTileClick(title)}
                  icon={pictogram}
                  showArrow={isMobile}
                >
                  {title}
                </MenuPrimary>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container className={styles.categoryGrid}>
            <AutocompleteResults
              onAutocompleteSearch={onAutocompleteSearch}
              onClickCuisine={onTileClick}
            />
          </Grid>
        )}
      </div>
    </div>
  );
}

SearchOverlay.propTypes = {
  onAutocompleteChange: PropTypes.func.isRequired,
  onAutocompleteSearch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onTileClick: PropTypes.func.isRequired,
  showCuisines: PropTypes.bool.isRequired,
};
